import "./EditCreativeModal.scss";
import { ABCDCompanyOptions } from "@blisspointmedia/bpm-types/dist/pgTables/ABCDCompanyOptions";
import { ABCDCreative } from "@blisspointmedia/bpm-types/dist/ABCD";
import { ABCDCreativeSettings } from "@blisspointmedia/bpm-types/dist/pgTables/ABCDCreativeSettings";
import { ABCDScoreObject } from "../ABCD/ABCDUtils";
import { FieldConfig as AddCreativeFieldConfig } from "../AddEditCreativeModal/AddCreativeConfigsUtils";
import { getMediatypeConfig } from "./EditCreativeConfigs";
import { MdDelete, MdInfoOutline, MdSave } from "react-icons/md";
import { Modal } from "react-bootstrap";
import { useCompanyInfo } from "../../redux/company";
import { useSetAreYouSure, useSetError } from "../../redux/modals";
import * as R from "ramda";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  ButtonFrameworkVariant,
  ButtonType,
  FullPageSpinner,
  PendingChangesPanel,
} from "../../Components";

interface EditCreativeModalProps {
  abcdCompanyOptions: ABCDCompanyOptions;
  abcdCreativeSettingsMap: Record<string, ABCDCreative>;
  abcdScoreMap: Record<string, ABCDScoreObject>;
  bulkEditCreatives: object[];
  creative: object;
  mediatype: string;
  onHide: () => void;
}

const EditCreativeModal = ({
  abcdCompanyOptions,
  abcdCreativeSettingsMap,
  abcdScoreMap,
  bulkEditCreatives,
  creative,
  onHide,
  mediatype,
}: EditCreativeModalProps): JSX.Element => {
  const [abcdCreativeSettings, setABCDCreativeSettings] = useState<ABCDCreativeSettings>();
  const [abcdScore, setABCDScore] = useState<ABCDScoreObject>();
  const [
    initialABCDCreativeSettings,
    setInitialABCDCreativeSettings,
  ] = useState<ABCDCreativeSettings>();
  const [initialABCDScore, setInitialABCDScore] = useState<ABCDScoreObject>();
  const [customAttributes, setCustomAttributes] = useState<
    Record<string, AddCreativeFieldConfig>
  >();
  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
  const [modifiedCreative, setModifiedCreative] = useState<object>();
  const [modifyingName, setModifyingName] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedSection, setSelectedSection] = useState<string>();
  const [showPendingChanges, setShowPendingChanges] = useState<boolean>(false);
  const companyInfo = useCompanyInfo();
  const setAreYouSure = useSetAreYouSure(true);
  const setError = useSetError();

  const currentMediatypeConfig = useMemo(() => getMediatypeConfig(mediatype), [mediatype]);
  const creativeID = useMemo(
    () =>
      creative && currentMediatypeConfig
        ? currentMediatypeConfig.getCreativeID(creative)
        : undefined,
    [creative, currentMediatypeConfig]
  );
  const numberOfChanges = useMemo(
    () =>
      currentMediatypeConfig && creative && modifiedCreative && initialABCDCreativeSettings
        ? currentMediatypeConfig.getCreativeChanges({
            abcdCreativeSettings,
            abcdScore,
            creative,
            initialABCDCreativeSettings,
            initialABCDScore,
            modifiedCreative,
          }).numChanges
        : 0,
    [
      abcdCreativeSettings,
      abcdScore,
      creative,
      currentMediatypeConfig,
      initialABCDCreativeSettings,
      initialABCDScore,
      modifiedCreative,
    ]
  );
  const sections = useMemo(
    () =>
      R.isNil(creative)
        ? {}
        : currentMediatypeConfig.getSections({
            abcdCompanyOptions,
            abcdCreativeSettings,
            abcdScore,
            company: companyInfo.cid,
            creative,
            customAttributes,
            expandedMap,
            modifiedCreative,
            onHide,
            setABCDCreativeSettings,
            setABCDScore,
            setAreYouSure,
            setError,
            setExpandedMap,
            setModifiedCreative,
            setSaving,
          }),
    [
      abcdCompanyOptions,
      abcdCreativeSettings,
      abcdScore,
      companyInfo,
      creative,
      currentMediatypeConfig,
      customAttributes,
      expandedMap,
      modifiedCreative,
      onHide,
      setABCDCreativeSettings,
      setABCDScore,
      setAreYouSure,
      setError,
      setExpandedMap,
      setModifiedCreative,
      setSaving,
    ]
  );

  const modalHeaderSection = useMemo(() => {
    if (
      creative &&
      modifiedCreative &&
      currentMediatypeConfig &&
      currentMediatypeConfig.renderModalHeader
    ) {
      return currentMediatypeConfig.renderModalHeader({
        bulkEditCreatives,
        modifiedCreative,
        modifyingName,
        sections,
        selectedSection,
        setModifiedCreative,
        setModifyingName,
        setSelectedSection,
      });
    }
    return <></>;
  }, [
    bulkEditCreatives,
    creative,
    currentMediatypeConfig,
    modifiedCreative,
    modifyingName,
    sections,
    selectedSection,
  ]);

  const save = useCallback(() => {
    if (currentMediatypeConfig.updateCreative) {
      currentMediatypeConfig.updateCreative({
        abcdCreativeSettings,
        abcdScore,
        bulkEditCreatives,
        company: companyInfo.cid,
        creative,
        customAttributes,
        initialABCDCreativeSettings,
        initialABCDScore,
        modifiedCreative,
        onHide,
        setAreYouSure,
        setError,
        setSaving,
      });
    }
  }, [
    abcdCreativeSettings,
    abcdScore,
    bulkEditCreatives,
    companyInfo,
    creative,
    currentMediatypeConfig,
    customAttributes,
    initialABCDCreativeSettings,
    initialABCDScore,
    modifiedCreative,
    onHide,
    setAreYouSure,
    setError,
    setSaving,
  ]);

  useEffect(() => {
    if (R.isNil(selectedSection && sections)) {
      setSelectedSection(R.keys(sections)[0] as string);
    }
  }, [selectedSection, sections, setSelectedSection]);

  useEffect(() => {
    (async () => {
      if (
        currentMediatypeConfig &&
        currentMediatypeConfig.getCustomAttributes &&
        R.isNil(customAttributes)
      ) {
        setCustomAttributes(await currentMediatypeConfig.getCustomAttributes(companyInfo.cid));
      }
    })();
  }, [companyInfo, currentMediatypeConfig, customAttributes]);

  useEffect(() => {
    if (creative && R.isNil(modifiedCreative)) {
      setModifiedCreative(creative);
    }
  }, [creative, modifiedCreative, setModifiedCreative]);

  useEffect(() => {
    if (R.isNil(abcdCreativeSettings) && creativeID && abcdCreativeSettingsMap[creativeID]) {
      setABCDCreativeSettings(abcdCreativeSettingsMap[creativeID]);
      setInitialABCDCreativeSettings(abcdCreativeSettingsMap[creativeID]);
    }
  }, [abcdCreativeSettingsMap, abcdCompanyOptions, abcdCreativeSettings, creativeID]);

  useEffect(() => {
    if (R.isNil(abcdScore) && creativeID && abcdScoreMap[creativeID]) {
      setABCDScore(abcdScoreMap[creativeID]);
      setInitialABCDScore(abcdScoreMap[creativeID]);
    }
  }, [abcdCompanyOptions, abcdScoreMap, abcdScore, creativeID]);

  const pendingChangesPanel = showPendingChanges && modifiedCreative && (
    <PendingChangesPanel
      headers={R.map(
        field => ({
          field,
          flex: 1,
          label: field,
          name: field,
          renderer: row =>
            typeof row[field] === "object"
              ? `${JSON.stringify(row[field], null, 2)}`
              : `${row[field]}`,
        }),
        R.filter(
          field => !R.equals(modifiedCreative[field], creative[field]),
          R.keys(creative)
        ) as string[]
      )}
      originalData={{
        [creativeID]: creative,
        ...R.indexBy(
          R.prop("id"),
          bulkEditCreatives.map((creative, index) => ({ ...creative, id: index }))
        ),
      }}
      pendingChanges={{
        editedRows: [
          { ...modifiedCreative, id: creativeID },
          ...bulkEditCreatives.map((bulkCreative, index) => {
            const modifiedBulkCreative = R.clone(bulkCreative);
            for (const key of R.keys(creative)) {
              if (!R.equals(modifiedCreative[key], creative[key])) {
                modifiedBulkCreative[key] = modifiedCreative[key];
              }
            }
            return { ...modifiedBulkCreative, id: index };
          }),
        ],
      }}
      pendingRowHeaderRender={row =>
        currentMediatypeConfig.getCreativeID && (
          <div>{currentMediatypeConfig.getCreativeID(row)}</div>
        )
      }
      setShowPendingChanges={setShowPendingChanges}
      showPendingChanges={showPendingChanges}
    />
  );

  const editCreativeModal = selectedSection && sections && (
    <Modal
      show
      onHide={onHide}
      centered
      className={`editCreativeModal ${saving ? "loading" : ""}`}
      size="lg"
    >
      <Modal.Header>{modalHeaderSection}</Modal.Header>
      <Modal.Body>{sections[selectedSection]}</Modal.Body>
      <Modal.Footer>
        {numberOfChanges > 0 && (
          <Button
            className="unsavedChanges"
            icon={<MdInfoOutline />}
            type={ButtonType.OUTLINED}
            variant={ButtonFrameworkVariant.LEADING_ICON}
            onClick={() => setShowPendingChanges(true)}
          >
            See {numberOfChanges} Unsaved Changes
          </Button>
        )}
        <Button
          icon={<MdDelete />}
          onClick={onHide}
          type={ButtonType.OUTLINED}
          variant={ButtonFrameworkVariant.TRAILING_ICON}
        >
          Discard Edits
        </Button>
        <Button
          icon={<MdSave />}
          onClick={save}
          type={ButtonType.FILLED}
          variant={ButtonFrameworkVariant.TRAILING_ICON}
        >
          Save Edits
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return showPendingChanges && pendingChangesPanel ? (
    pendingChangesPanel
  ) : editCreativeModal ? (
    editCreativeModal
  ) : (
    <FullPageSpinner />
  );
};

export default EditCreativeModal;
